"use client";
import React from "react";
import { AppointmentForm } from "@/lib/hooks/useAppointment";
import { formatDatetime } from "@/lib/helpers";
import BookingServiceSummary from "@/app/components/BookingServiceSummary";
import {
  MEMBER_TAG_NOTES_LABEL,
  MemberTagBadges,
} from "@/app/components/MemberTags";
import { useRepricedServices } from "../useRepricedServices";
import ServicePricingStatus from "./ServicePricingStatus";
import styles from "./AppointmentReview.module.scss";

/**
 * Pre-submit booking summary. Each service keeps its title, price and duration
 * together without repeating the service in a separate price section.
 * Booking-critical descriptions are shown directly below the service they
 * describe, including custom pricing instructions. The services here are the
 * stylist's own clones (member price, duration, description, `up`), so a
 * starting-at base price renders `$60+`, while a chosen option's price is exact
 * and the option's description wins over the service's.
 *
 * A cross-business stylist's slot can book against a different Openings
 * record than the one the canonical menu priced (Task 17: Edition Studio
 * lists the same service at a different price/duration per member).
 * The modal-level pricing controller resolves a cross-business service menu
 * once and shares it with this review, phone routing and `SetupPayment`.
 * Price-sensitive rows stay hidden while that authoritative lookup is pending
 * or failed; the status alert owns the retry rather than displaying a
 * canonical price the booked business may not honor.
 */
const AppointmentReview = ({
  appointment,
}: {
  appointment: AppointmentForm;
}) => {
  const pricing = useRepricedServices();
  const services = pricing.services;
  const isPricingReady = pricing.status === "ready";

  if (!appointment) return null;
  const datetime = formatDatetime(appointment.date, appointment.time);

  return (
    <section className={styles.review} aria-label="Appointment details">
      <div className={styles.fact}>
        <strong>Stylist:</strong> {appointment.stylistName}
        {/* The stylist's Openings tags, before the customer commits so they
            can still choose someone else (booking-flow standard 2.4.1 § 12). */}
        <MemberTagBadges
          tags={appointment.memberTags}
          label={MEMBER_TAG_NOTES_LABEL}
          className={styles.memberTags}
        />
      </div>

      <ServicePricingStatus />

      {isPricingReady && (
        <section className={styles.servicesSection} aria-label="Service summary">
          <h3>{services.length === 1 ? "Service:" : "Services:"}</h3>
          <BookingServiceSummary services={services} showDescriptions />
        </section>
      )}

      <p className={styles.fact}>
        <strong>At:</strong> {datetime}
      </p>

      {appointment.locationName && (
        <p className={styles.fact}>
          <strong>Location:</strong> {appointment.locationName}
        </p>
      )}
    </section>
  );
};

export default AppointmentReview;
